import { isTooManyRequestsError } from './errors';

export const DOWNLOAD_RATE_LIMITED = 'downloadRateLimited';
const failure = code => Object.assign(new Error(code), { code });

/** Signed links expire conservatively, measured from request start, never response receipt. */
export const downloadUrlExpiry = (url, startedAt) => {
  const parsed = new URL(url);
  if (parsed.protocol !== 'https:' || !parsed.hostname) throw failure('invalidDownloadUrl');
  const date = parsed.searchParams.get('X-Amz-Date');
  const seconds = Number(parsed.searchParams.get('X-Amz-Expires'));
  const match = date?.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
  const signedAt = match
    ? Date.UTC(...match.slice(1).map((v, i) => Number(v) - (i === 1 ? 1 : 0)))
    : NaN;
  return Number.isFinite(signedAt) && seconds > 0
    ? Math.min(startedAt + 570000, signedAt + seconds * 1000 - 30000)
    : startedAt + 570000;
};

/** One scheduler per SDK/user context. Cancellation belongs to consumers, not requests. */
export const createFileDownloadCache = () => {
  const cache = new Map();
  const jobs = new Map();
  let history = [];
  let timer;
  let running = false;
  let destroyed = false;
  let cooldown = 0;
  let lastStart = -Infinity;
  const settle = (job, error, url, expiresAt) => {
    job.consumers.forEach(c => {
      c.signal?.removeEventListener('abort', c.abort);
      if (error) c.reject(error);
      else {
        c.onExpiry?.(expiresAt);
        c.resolve(url);
      }
    });
    job.consumers.clear();
  };
  const schedule = (delay = 0) => {
    clearTimeout(timer);
    if (!destroyed) timer = setTimeout(pump, delay);
  };
  const pump = () => {
    if (destroyed || running) return;
    const now = Date.now();
    history = history.filter(h => h.at > now - 60000);
    for (const [key, job] of jobs) if (!job.started && !job.consumers.size) jobs.delete(key);
    const candidates = [...jobs.values()].filter(j => !j.started && j.consumers.size);
    candidates.forEach(j => {
      j.explicit = [...j.consumers].some(c => c.purpose !== 'preview');
    });
    candidates.sort((a, b) => Number(b.explicit) - Number(a.explicit));
    const job = candidates.find(
      j => j.explicit || typeof document === 'undefined' || !document.hidden
    );
    if (!job) return;
    const automatic = history.filter(h => !h.explicit && h.threadId === job.threadId);
    const wait = Math.max(
      0,
      cooldown - now,
      lastStart + 3500 - now,
      history.length >= 18 ? history[0].at + 60000 - now : 0,
      !job.explicit && automatic.length >= 12 ? automatic[0].at + 60000 - now : 0
    );
    if (wait) {
      schedule(wait);
      return;
    }
    job.started = true;
    running = true;
    lastStart = now;
    history.push({ at: now, explicit: job.explicit, threadId: job.threadId });
    Promise.resolve()
      .then(job.createUrl)
      .then(url => {
        const expiresAt = downloadUrlExpiry(url, now);
        if (expiresAt <= Date.now()) throw failure('expiredDownloadUrl');
        if (!destroyed && !job.invalidated) {
          for (const [key, entry] of cache) if (entry.expiresAt <= Date.now()) cache.delete(key);
          cache.delete(job.key);
          cache.set(job.key, { url, expiresAt });
          while (cache.size > 200) cache.delete(cache.keys().next().value);
          settle(job, null, url, expiresAt);
        }
      })
      .catch(error => {
        if (isTooManyRequestsError(error)) {
          const headers = error?.response?.headers || error?.headers;
          const retry =
            headers?.get?.('Retry-After') ?? headers?.['retry-after'] ?? headers?.['Retry-After'];
          const duration = Number(retry) * 1000 || Date.parse(retry) - Date.now() || 0;
          cooldown = Date.now() + Math.max(60000, Number.isFinite(duration) ? duration : 0);
          jobs.forEach(j => settle(j, failure(DOWNLOAD_RATE_LIMITED)));
          jobs.clear();
        } else settle(job, error);
      })
      .finally(() => {
        if (jobs.get(job.key) === job) jobs.delete(job.key);
        running = false;
        schedule();
      });
  };
  const visibility = () => schedule();
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', visibility);
  return {
    getDownloadUrl({ key, threadId, purpose, createUrl, signal, onExpiry }) {
      if (destroyed || signal?.aborted) return Promise.reject(failure('aborted'));
      const entry = cache.get(key);
      if (entry?.expiresAt > Date.now()) {
        cache.delete(key);
        cache.set(key, entry);
        onExpiry?.(entry.expiresAt);
        return Promise.resolve(entry.url);
      }
      cache.delete(key);
      if (cooldown > Date.now()) return Promise.reject(failure(DOWNLOAD_RATE_LIMITED));
      let job = jobs.get(key);
      if (!job) {
        if (jobs.size >= 64 && purpose !== 'preview') {
          const automatic = [...jobs.values()]
            .reverse()
            .find(j => !j.started && [...j.consumers].every(c => c.purpose === 'preview'));
          if (automatic) {
            settle(automatic, failure('previewQueueFull'));
            jobs.delete(automatic.key);
          }
        }
        if (jobs.size >= 64) return Promise.reject(failure('previewQueueFull'));
        job = { key, threadId, createUrl, consumers: new Set(), explicit: purpose !== 'preview' };
        jobs.set(key, job);
      } else if (!job.started && purpose !== 'preview') job.explicit = true;
      return new Promise((resolve, reject) => {
        const consumer = { resolve, reject, signal, threadId, purpose, onExpiry };
        consumer.abort = () => {
          job.consumers.delete(consumer);
          signal?.removeEventListener('abort', consumer.abort);
          reject(failure('aborted'));
          // Defer removal until pump: StrictMode's remount can join the same job.
          schedule();
        };
        job.consumers.add(consumer);
        signal?.addEventListener('abort', consumer.abort, { once: true });
        for (const [k, j] of jobs) if (!j.started && !j.consumers.size && j !== job) jobs.delete(k);
        schedule();
      });
    },
    invalidateDownloadUrl(key) {
      cache.delete(key);
      const job = jobs.get(key);
      if (job) {
        job.invalidated = true;
        settle(job, failure('unavailable'));
        jobs.delete(key);
      }
    },
    cancelThread(threadId) {
      jobs.forEach(job => {
        [...job.consumers].filter(c => c.threadId === threadId).forEach(c => c.abort());
        if (!job.started && !job.consumers.size) jobs.delete(job.key);
      });
    },
    destroy() {
      destroyed = true;
      clearTimeout(timer);
      if (typeof document !== 'undefined')
        document.removeEventListener('visibilitychange', visibility);
      jobs.forEach(j => {
        j.invalidated = true;
        settle(j, failure('aborted'));
      });
      jobs.clear();
      cache.clear();
    },
  };
};
